import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import { accent, alert, gold, graphite, honor, steel } from './palette'

// Aura's severities read from its primitive hues (success → green, warn →
// orange, help → purple, danger → red, info → sky), so remapping the
// primitives is what makes every Tag, Message and Button follow the palette.
const primitive = {
  green: accent,
  emerald: accent,
  lime: accent,
  orange: gold,
  amber: gold,
  yellow: gold,
  purple: honor,
  violet: honor,
  fuchsia: honor,
  pink: honor,
  red: alert,
  rose: alert,
  sky: steel,
  blue: steel,
  cyan: steel,
  teal: steel,
  indigo: steel,
  slate: graphite,
  gray: graphite,
  zinc: graphite,
  neutral: graphite,
  stone: graphite,
}

// A solid severity button: `shade` is the resting fill, the next two darken
// on hover and press.
function solid(hue: string, shade: 400 | 600 | 700, color: string) {
  const step = shade === 400 ? -100 : 100
  const hover = `{${hue}.${shade + step}}`
  const active = `{${hue}.${shade + step * 2}}`
  return {
    background: `{${hue}.${shade}}`,
    hoverBackground: hover,
    activeBackground: active,
    borderColor: `{${hue}.${shade}}`,
    hoverBorderColor: hover,
    activeBorderColor: active,
    color,
    hoverColor: color,
    activeColor: color,
    focusRing: { color: `{${hue}.${shade}}`, shadow: 'none' },
  }
}

const Krewebacchus = definePreset(Aura, {
  primitive,
  semantic: {
    primary: accent,
    colorScheme: {
      light: {
        surface: graphite,
        primary: {
          color: '{primary.700}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.800}',
          activeColor: '{primary.900}',
        },
        highlight: {
          background: '{primary.50}',
          focusBackground: '{primary.100}',
          color: '{primary.800}',
          focusColor: '{primary.900}',
        },
        text: {
          color: '{surface.900}',
          hoverColor: '{surface.950}',
          mutedColor: '{surface.600}',
          hoverMutedColor: '{surface.700}',
        },
      },
      dark: {
        surface: graphite,
        primary: {
          color: '{primary.400}',
          contrastColor: '{primary.950}',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}',
        },
        text: {
          color: '#e3eae4',
          hoverColor: '#ffffff',
          mutedColor: '{surface.400}',
          hoverMutedColor: '{surface.300}',
        },
        content: {
          borderColor: '{surface.800}',
        },
        formField: {
          borderColor: '{surface.700}',
          hoverBorderColor: '{surface.600}',
          // A step dimmer than muted text, so an empty field never reads as
          // filled in (Aura's dark default is the muted color)
          placeholderColor: '{surface.500}',
        },
      },
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            success: solid('green', 700, '#ffffff'),
            warn: solid('orange', 400, '{orange.950}'),
            help: solid('purple', 600, '#ffffff'),
            danger: solid('red', 600, '#ffffff'),
            info: solid('sky', 600, '#ffffff'),
          },
        },
      },
    },
  },
})

export default {
  preset: Krewebacchus,
  options: {
    prefix: 'p',
    darkModeSelector: '.dark',
    cssLayer: false,
  },
}
